import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import {
  BasicTable,
  Button,
  CheckCircleIcon,
  ContentBox,
  ContentPage,
  InputTextarea,
  ModalForm,
  NoResults,
  Text,
  Tooltip,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "../../../../hooks/useService";
import { escapeHtml, formatBRL, formatCompetencia, formatDate, usePageHeader } from "@components/ow";

const Actions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const Reason = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const competenceOf = (input) => ({ month: Number(input.reference_month), year: Number(input.reference_year) });

const REASON_MAX_LENGTH = 500;

export default function ApproveValues() {
  const valueInputService = useService("contract-value-inputs");
  const currentUser = JSON.parse(localStorage.getItem("user_ow") || "null");
  // "Ver cronogramas" vê os pendentes; aprovar e rejeitar seguem só ADM
  const isAdmin = currentUser?.role === "ADM";
  const [loading, setLoading] = useState(true);
  const [pendingInputs, setPendingInputs] = useState([]);
  const [approvingId, setApprovingId] = useState(null);
  const [selectedInput, setSelectedInput] = useState(null);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const busy = approvingId !== null || rejecting;

  const title = isAdmin ? "Aprovar valores variáveis" : "Valores variáveis pendentes";
  usePageHeader({
    title,
    breadcrumb: [
      { page: "Contratos", route: "/c/contracts" },
      { page: title, route: "/c/contracts/value-approvals" },
    ],
  });

  useEffect(() => {
    fetchPendingInputs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchPendingInputs() {
    setLoading(true);
    try {
      const result = await valueInputService.listPending("PENDING");
      if (result && result.data) {
        setPendingInputs(result.data);
      } else {
        throw new Error("Erro ao carregar inputs pendentes");
      }
    } catch (error) {
      console.error(error);
      const errorMessage =
        error?.response?.data?.msg ||
        error?.message ||
        "Erro ao carregar inputs pendentes";
      toast.error(errorMessage);
      setPendingInputs([]);
    }
    setLoading(false);
  }

  async function handleApprove(inputId) {
    setApprovingId(inputId);
    try {
      const result = await valueInputService.approve(inputId);
      if (result && !result.error) {
        toast.success("Valor aprovado com sucesso!");
        fetchPendingInputs();
      } else {
        throw new Error(result?.msg || "Erro ao aprovar valor");
      }
    } catch (error) {
      console.error(error);
      const errorMessage =
        error?.response?.data?.msg ||
        error?.message ||
        "Erro ao aprovar valor";
      toast.error(errorMessage);
    }
    setApprovingId(null);
  }

  function openReject(input) {
    setSelectedInput(input);
    setReason("");
    setReasonError("");
  }

  function closeReject() {
    if (rejecting) return;
    setSelectedInput(null);
  }

  async function handleReject() {
    // O back só exige motivo não vazio: a tela não recusa o que ele aceita
    if (!reason.trim()) {
      setReasonError("Informe o motivo.");
      return;
    }

    setRejecting(true);
    try {
      const result = await valueInputService.reject(selectedInput.id, reason.trim());
      if (result && !result.error) {
        toast.success("Valor rejeitado");
        setSelectedInput(null);
        fetchPendingInputs();
      } else {
        throw new Error(result?.msg || "Erro ao rejeitar valor");
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.message || "Erro ao rejeitar valor");
    }
    setRejecting(false);
  }

  const columns = [
    {
      id: "contractor",
      header: "Prestador",
      size: 220,
      maxSize: 280,
      cell: ({ row }) => (
        <div>
          <Text $text={escapeHtml(row.original.contract?.contractor?.name || "—")} $variant="paragraph_small_bold" $color="N900" />
          {row.original.contract?.contractor?.company_name ? (
            <Text $text={escapeHtml(row.original.contract.contractor.company_name)} $variant="caption_medium" $color="N400" />
          ) : null}
        </div>
      ),
    },
    { id: "competence", header: "Competência", size: 110, cell: ({ row }) => formatCompetencia(competenceOf(row.original)) },
    {
      id: "value",
      header: "Valor",
      size: 130,
      align: "right",
      cell: ({ row }) => <Text $text={formatBRL(row.original.value)} $variant="paragraph_small_bold" $color="N900" />,
    },
    {
      id: "inputtedBy",
      header: "Informado por",
      size: 220,
      maxSize: 280,
      cell: ({ row }) => (
        <div>
          <Text $text={escapeHtml(row.original.inputtedBy?.name || "—")} $variant="paragraph_small_medium" $color="N900" />
          {row.original.inputtedBy?.email ? (
            <Text $text={escapeHtml(row.original.inputtedBy.email)} $variant="caption_medium" $color="N400" />
          ) : null}
        </div>
      ),
    },
    { id: "inputtedAt", header: "Informado em", size: 120, cell: ({ row }) => formatDate(row.original.inputted_at) },
    { id: "notes", header: "Observações", size: 200, maxSize: 320, cell: ({ row }) => row.original.notes || "—" },
  ];

  if (isAdmin) {
    columns.push({
      id: "actions",
      size: 220,
      maxSize: 220,
      align: "right",
      cell: ({ row }) => {
        const input = row.original;
        const ownInput = input.inputtedBy?.id === currentUser?.id;
        const approve = (
          <Button
            $text="Aprovar"
            $variant="outline"
            $size="small"
            $noWidth
            $isLoading={approvingId === input.id}
            $isDisabled={busy || ownInput}
            $action={() => handleApprove(input.id)}
          />
        );
        return (
          <Actions>
            {ownInput ? (
              <Tooltip
                content="Você não pode aprovar um valor que você mesmo submeteu"
                $position="top"
                $size="medium"
                $color="dark">
                {approve}
              </Tooltip>
            ) : (
              approve
            )}
            <Button
              $text="Reprovar"
              $variant="outline_danger"
              $size="small"
              $noWidth
              $isDisabled={busy}
              $action={() => openReject(input)}
            />
          </Actions>
        );
      },
    });
  }

  return (
    <ContentPage>
      <ContentBox>
        <BasicTable
          data={pendingInputs}
          columns={columns}
          getRowId={(row) => String(row.id)}
          isLoading={loading}
          noResults={<NoResults notResultText="Nenhum valor pendente de aprovação" notResultIcon={<CheckCircleIcon />} />}
        />
      </ContentBox>

      {selectedInput && (
        <ModalForm
          $showModal={!!selectedInput}
          $setShowModal={closeReject}
          $noClose={rejecting}
          $isLoading={rejecting}
          $size="medium"
          $title={`Reprovar valor de ${formatCompetencia(competenceOf(selectedInput), true)}`}
          $contentBackground="N0"
          $actions={[
            { text: "Cancelar", action: closeReject, variant: "outline", isDisabled: rejecting },
            { text: "Reprovar valor", action: handleReject, variant: "danger", isDisabled: rejecting },
          ]}>
          <Reason>
            <Text
              $text="%s · %s · <strong>%s</strong>"
              $replaces={[
                escapeHtml(selectedInput.contract?.contractor?.name || "Prestador"),
                formatCompetencia(competenceOf(selectedInput)),
                formatBRL(selectedInput.value),
              ]}
              $variant="paragraph_small_medium"
              $color="N700"
              $colorEmphasis="N900"
            />
            <InputTextarea
              $idForm="rejection-reason"
              $label="Motivo da reprovação"
              $variant="default"
              $isRequired
              $errors={reasonError}
              $description="Quem informou o valor vê este motivo no histórico."
              maxLength={REASON_MAX_LENGTH}
              rows={3}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (reasonError) setReasonError("");
              }}
            />
          </Reason>
        </ModalForm>
      )}
    </ContentPage>
  );
}
